import {
  canonicalEnumValue,
  csvSchemaFromServer,
  joinList,
  recordKey,
  splitList,
  type CsvSchema,
  type ServerImportSchema,
  type DiffChange,
  type SelectedChanges
} from '~/components/csv-import';
import {
  ImportColumnOptionsSource,
  VoiceAgentTransferCallType,
  VoiceAgentTransferDirectoryEntryType
} from '~/api/graphql';
import {
  type VoiceAgentSettingsTransferDirectoryEntryInput,
  type VoiceAgentTransferLineOfBusinessInput
} from './voice_agent_form_input';

// CSV import for the transfer directory. Committing rewrites the settings form's
// array; nothing is persisted until the user saves the page.

// Must match validateTransferConfiguration in voice_agent.go; import_parity.json holds
// both sides to it.
const CALL_TYPE_REQUIRED = 'At least one call type is required.';

/**
 * The server's transfer directory columns, with the page's behaviour on top. `catalog`
 * is the line-of-business catalog on the page, which may hold unsaved edits, so the
 * template lists the lines of business the save will accept. While the page's External
 * Transfer switch is on, the save refuses an external entry with no call types, so the
 * import flags one.
 */
export const transferDirectorySchema = (
  server: ServerImportSchema,
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[],
  externalTransferEnabled = false
): CsvSchema =>
  csvSchemaFromServer(server, {
    behaviours: {
      name: { example: 'Jordan Demo' },
      number: { extensionField: 'extension' },
      // DTMF after connect: digits, * and #, and a comma to pause.
      extension: { allow: /[\d*#,]/, example: '204', width: 'xs', shortLabel: 'Ext.' },
      // A save reads a blank type as internal; the import asks for one so a skipped
      // cell doesn't quietly file an external contact as internal.
      entryType: { required: true },
      department: { example: 'Claims' },
      transferCriteria: { example: 'Caller wants to file or check on a claim', width: 'lg' },
      callTypes: {
        requiredFor: (row) =>
          externalTransferEnabled &&
          row.entryType.trim().toUpperCase() === VoiceAgentTransferDirectoryEntryType.External
            ? CALL_TYPE_REQUIRED
            : undefined
      },
      aliases: { example: 'Jordan;Claims desk' },
      servicingCode: { example: 'CSR01' }
    },
    options: {
      // Spreadsheets carry labels ("Commercial"), not ids.
      [ImportColumnOptionsSource.LineOfBusinessCatalog]: lineOfBusinessCatalog.map(
        (line) => line.label
      )
    }
  });

export const entryToRecord = (
  entry: VoiceAgentSettingsTransferDirectoryEntryInput,
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[]
): Record<string, string> => ({
  name: entry.name ?? entry.label ?? '',
  number: entry.number ?? '',
  extension: entry.extension ?? '',
  entryType: entry.entryType || VoiceAgentTransferDirectoryEntryType.Internal,
  department: entry.department ?? '',
  transferCriteria: entry.transferCriteria ?? '',
  callTypes: joinList(entry.callTypes),
  lineOfBusinessIds: joinList(
    (entry.lineOfBusinessIds ?? []).map(
      (id) => lineOfBusinessCatalog.find((line) => line.id === id)?.label ?? id
    )
  ),
  aliases: joinList(entry.aliases),
  servicingCode: entry.servicingCode ?? ''
});

/**
 * Keeps the fields of `existing` the schema doesn't describe. A schema field whose column
 * the file lacks reads as empty, so the import unsets it. `label` (API-required) comes
 * from the name.
 */
export const recordToEntry = (
  schema: CsvSchema,
  record: Record<string, string>,
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[],
  existing?: VoiceAgentSettingsTransferDirectoryEntryInput
): VoiceAgentSettingsTransferDirectoryEntryInput => {
  const fields = schema.fields;
  const field = (key: string) => fields.find((candidate) => candidate.key === key);

  // Validation is case-insensitive, so write the schema's spelling or the API
  // rejects the enum on save.
  const options = (key: string): string[] => {
    const declared = field(key);
    return splitList(record[key] ?? '').map(
      (value) => (declared && canonicalEnumValue(declared, value)) || value
    );
  };

  const entryType = field('entryType');
  return {
    ...existing,
    label: record.name || existing?.label || '',
    name: record.name || undefined,
    number: record.number ?? '',
    extension: record.extension || undefined,
    entryType:
      (((entryType && canonicalEnumValue(entryType, record.entryType ?? '')) ||
        record.entryType) as VoiceAgentTransferDirectoryEntryType) ||
      VoiceAgentTransferDirectoryEntryType.Internal,
    department: record.department || undefined,
    transferCriteria: record.transferCriteria ?? '',
    callTypes: options('callTypes') as VoiceAgentTransferCallType[],
    lineOfBusinessIds: options('lineOfBusinessIds').map(
      (label) => lineOfBusinessCatalog.find((line) => line.label === label)?.id ?? label
    ),
    aliases: splitList(record.aliases ?? ''),
    servicingCode: record.servicingCode || undefined
  };
};

/** Updates rewrite entries in place so an import keeps the agency's ordering. */
export const applyChangesToDirectory = (
  schema: CsvSchema,
  directory: VoiceAgentSettingsTransferDirectoryEntryInput[],
  changes: SelectedChanges,
  lineOfBusinessCatalog: VoiceAgentTransferLineOfBusinessInput[]
): VoiceAgentSettingsTransferDirectoryEntryInput[] => {
  // `schema` must be the one the diff used, or updates land on the wrong entry.
  const keyOfEntry = (entry: VoiceAgentSettingsTransferDirectoryEntryInput) =>
    recordKey(schema, entryToRecord(entry, lineOfBusinessCatalog));

  const updatedByKey = new Map(
    changes.updated.map((change: DiffChange) => [change.key, change.values ?? {}])
  );
  const removedKeys = new Set(changes.removed.map((change: DiffChange) => change.key));

  const kept = directory
    .filter((entry) => !removedKeys.has(keyOfEntry(entry)))
    .map((entry) => {
      const values = updatedByKey.get(keyOfEntry(entry));
      return values ? recordToEntry(schema, values, lineOfBusinessCatalog, entry) : entry;
    });

  return [
    ...kept,
    ...changes.added.map((change) =>
      recordToEntry(schema, change.values ?? {}, lineOfBusinessCatalog)
    )
  ];
};

/** Which entry a row is, by the key columns of `schema`. */
export const entryIdentity = (
  schema: CsvSchema,
  entry: VoiceAgentSettingsTransferDirectoryEntryInput
): string => recordKey(schema, entryToRecord(entry, []));
